/* "What Ceiling found in the file" — the parse listing, collapsed.
 *
 * This is the sections-and-tables listing that used to fill the confirmation
 * page a reviewer had to pass through before seeing anything. It is real
 * information — a table read wrongly is how a wrong finding gets made — but it
 * is not what a reviewer came for, so it lives here, closed, next to the
 * document rather than in front of it.
 *
 * A table the parse is unsure of is marked, and anything worth knowing about
 * the read — no numbered headings, no tables, a thin text layer — is said here,
 * because step 12 removed the confirmation screen those warnings used to live on.
 *
 * What is *not* here, since step 12: the checks that did not run. Those were
 * listed both here and in the findings list, saying the same thing twice in two
 * places with two levels of detail. They belong to the findings list — that is
 * where a reviewer decides what a silent check means — and this panel describes
 * the parse. One fact, one home. */

import { Disclosure, Label, Tag } from '../primitives';
import type { SourceDocument } from '../../types';
import './ParseDetail.css';

export interface ParseDetailProps {
  document: SourceDocument;
  /** Open on first render. Collapsed is the default. */
  defaultOpen?: boolean;
}

export default function ParseDetail({
  document: doc,
  defaultOpen = false,
}: ParseDetailProps) {
  const uncertain = doc.tables.filter((t) => t.confidence === 'low').length;

  // No hint on the control: the label is long and the column it sits in is
  // narrow, and the counts are the first thing inside anyway.
  return (
    <Disclosure
      className="pd"
      defaultOpen={defaultOpen}
      summary="What Ceiling found in the file"
    >
      <div className="pd__body">
        {(doc.warnings ?? []).length > 0 ? (
          <div className="pd__block">
            <Label tone="amber">Worth knowing about this document</Label>
            {(doc.warnings ?? []).map((warning) => (
              <div key={warning.reason} className="pd__warning">
                <h4 className="pd__warning-head">{warning.message}</h4>
                <p className="pd__warning-body">{warning.detail}</p>
                <p className="pd__warning-action">{warning.action}</p>
              </div>
            ))}
          </div>
        ) : null}

        <div className="pd__block">
          <div className="pd__block-head">
            <Label mono>Sections</Label>
            <Tag>{doc.sections.length}</Tag>
          </div>
          {doc.sections.length === 0 ? (
            <p className="pd__none">
              No numbered sections were found. The checks still ran against the
              text.
            </p>
          ) : (
            <ul className="pd__rows">
              {doc.sections.map((section) => (
                <li key={section.id} className="pd__row">
                  <span className="pd__row-num">{section.number || '—'}</span>
                  <span className="pd__row-title">{section.title}</span>
                  <span className="pd__row-meta">
                    {section.startPage === section.endPage
                      ? `p${section.startPage}`
                      : `p${section.startPage}–${section.endPage}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="pd__block">
          <div className="pd__block-head">
            <Label mono>Tables</Label>
            <Tag>{doc.tables.length}</Tag>
            {uncertain > 0 ? <Tag tone="amber">{uncertain} uncertain</Tag> : null}
          </div>
          {doc.tables.length === 0 ? (
            <p className="pd__none">
              No tables were read out of this file. Checks that compare tables
              could not run.
            </p>
          ) : (
            <ul className="pd__rows">
              {doc.tables.map((table) => (
                <li key={table.id} className="pd__row">
                  <span className="pd__row-num">{table.label}</span>
                  <span className="pd__row-title">{table.caption || '—'}</span>
                  <span className="pd__row-meta">
                    p{table.page} · {table.rows.length}×{table.headers.length}
                  </span>
                  {table.confidence === 'low' ? (
                    <span className="pd__row-sub">
                      Uncertain: {table.confidenceNotes.join(' ')}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Disclosure>
  );
}
